import React from 'react'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { Header } from 'app/KinerjaShared/components/Header'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { usePelaksanaanPraktikKinerjaSummary } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaSummary'
import PelaksanaanPraktikKinerjaStages from 'app/KinerjaPSPegawaiPage/components/PelaksanaanPraktikKinerjaStages'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePSStatus } from 'app/KinerjaPSPegawaiPage/hooks/useSummary'

import type { StatusPelaksanaanPraktikKinerja } from 'types/kinerja-ps-pegawai'

const STAGES: StatusPelaksanaanPraktikKinerja[][] = [
  ['DISKUSI_PERSIAPAN'],
  ['PENDAMPINGAN_SEKOLAH'],
  ['DISKUSI_TINDAK_LANJUT', 'DISKUSI_TINDAK_LANJUT_PENILAIAN'],
  ['UPAYA_TINDAK_LANJUT'],
  ['REFLEKSI_TINDAK_LANJUT'],
  ['REFLEKSI_TINDAK_LANJUT_PENILAIAN', 'SELESAI'],
]

const PSPelaksanaanPraktikKinerjaPage = () => {
  const { summary, summaryFetchStatus } = usePSStatus()
  const {
    pelaksanaanPraktikKinerjaSummaryFetchStatus,
    pelaksanaanPraktikKinerjaSummary,
  } = usePelaksanaanPraktikKinerjaSummary()
  const currentState = React.useMemo(
    () => pelaksanaanPraktikKinerjaSummary?.praktikKinerjaPelaksanaanStatus,
    [pelaksanaanPraktikKinerjaSummary]
  )
  const appealState = React.useMemo(
    () => summary?.pelaksanaanStatuses?.praktikKinerjaPerubahanStatus,
    [summary]
  )
  const router = useRouter()
  const toggle = useToggleSdkContext()
  const ratingEnabled = toggle?.['kinerja-ps-pegawai-rating-enabled'] || false

  const isScoringStage = React.useMemo(
    () => currentState === 'REFLEKSI_TINDAK_LANJUT_PENILAIAN',
    [currentState]
  )
  const isFinishedStage = React.useMemo(
    () => currentState === 'SELESAI',
    [currentState]
  )
  const isLoading = React.useMemo(
    () =>
      pelaksanaanPraktikKinerjaSummaryFetchStatus === 'loading' ||
      summaryFetchStatus === 'loading',
    [pelaksanaanPraktikKinerjaSummaryFetchStatus, summaryFetchStatus]
  )
  const isSuccess = React.useMemo(
    () =>
      pelaksanaanPraktikKinerjaSummaryFetchStatus === 'success' &&
      summaryFetchStatus === 'success',
    [pelaksanaanPraktikKinerjaSummaryFetchStatus, summaryFetchStatus]
  )

  return (
    <div className="pb-20 pl-64">
      <KinerjaSidebar sticky activeMenu="ps-pegawai" />
      <div className="w-full gap-4 p-6 mx-auto mt-4 space-y-6 max-w-7xl">
        <Header
          title="Praktik Kinerja"
          className="mb-10 print:hidden"
          onBack={router.back}
        />

        {isLoading && (
          <div className="flex justify-center">
            <Spinner data-testid="loading-summary" />
          </div>
        )}

        {isSuccess && (
          <>
            <PelaksanaanPraktikKinerjaStages
              leftSectionTitle="Tahapan Pelaksanaan"
              rightSectionTitle="Indikator pilihan"
              currentState={currentState}
              appealState={appealState}
              praktikKinerjaRhkPs={pelaksanaanPraktikKinerjaSummary?.rhkPs}
              stages={STAGES}
            />

            {ratingEnabled && (
              <div className="grid grid-cols-12">
                <div className="col-span-9 space-y-3">
                  <Text variant="heading-sm">Rating Praktik Kinerja</Text>
                  <SimpleCard className="p-4 space-y-3">
                    {isFinishedStage ? (
                      <div
                        data-testid="tsts"
                        className="inline-block p-2 rounded-md bg-surface-success-default"
                      >
                        <Text variant="heading-sm">
                          {
                            pelaksanaanPraktikKinerjaSummary?.ratingPraktikKinerja
                          }
                        </Text>
                      </div>
                    ) : (
                      <Badge
                        variant={isScoringStage ? 'informational' : 'neutral'}
                        className="w-fit"
                      >
                        {isScoringStage
                          ? 'Sedang dinilai'
                          : 'Tersedia setelah tahapan selesai'}
                      </Badge>
                    )}

                    <Text color="subdued" variant="body-sm">
                      Berdasarkan Diskusi Tindak Lanjut dan Refleksi Tindak
                      Lanjut.
                    </Text>
                  </SimpleCard>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default PSPelaksanaanPraktikKinerjaPage
